import { type FC, useEffect, useMemo } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useWatch } from 'react-hook-form';

import { Label } from '@wf/sw-ui-kit';

import { useLicenseContext } from '../../LicenseModeContext';
import { LicenseField } from '../LicenseField';

import useLicenseAbbreviation from '../../../../hooks/useLicenseAbbreviation';
import useLicenseTypeNature from '../../../../hooks/useLicenseTypeNature';
import { type LicenseTypeNature } from '../../../../types';
import { HeaderTabFields } from '../../types/fieldEnums';

import { featureFlagsSelector, isChildLpcoSelector, relatedProductsSelector } from '../../store/selectors';
import { resetConfiguration, resetErrors, setLpco, setSelectedLicenseTypeId } from '../../store/slice';
import { licenseTypesSelector } from '../../../../store/licenseType/selectors';
import { getLicenseTypes } from '../../../../store/licenseType/actions';
import { resetLicenseTypes } from '../../../../store/licenseType/slice';
import { getLicenseTypeByCode } from '../../store/actions';
import { licenseDataToOptionsList } from '../../helpers';

import styles from './HeaderTab.module.scss';

const MainInfoFields: FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { createMode } = useLicenseContext();
  const licenseTypeNature = useLicenseTypeNature();
  const licenseAbbreviation = useLicenseAbbreviation();

  const relatedProducts = useSelector(relatedProductsSelector);
  const featureFlags = useSelector(featureFlagsSelector);
  const isChildLpco = useSelector(isChildLpcoSelector);
  const { data: licenseTypes, loading: licenseTypesLoading } = useSelector(licenseTypesSelector(licenseTypeNature));
  const licenseTypeOptions = useMemo(() => licenseDataToOptionsList(licenseTypes), [licenseTypes]);

  const flow = useWatch({ name: HeaderTabFields.flow });

  useEffect(() => {
    if (createMode) {
      dispatch(getLicenseTypes({ licenseTypeNature, isCreatePage: true }));

      return () => {
        dispatch(resetLicenseTypes(licenseTypeNature as LicenseTypeNature));
      };
    }
  }, [dispatch, createMode, licenseTypeNature]);

  const handleLicenseChange = (licenseType: Nullable<SelectOption & { id: number }>) => {
    if (licenseType?.value) {
      dispatch(
        getLicenseTypeByCode({
          licenseTypeCode: licenseType.value,
          id: licenseType.id,
          generateConfiguration: true,
        })
      );
    } else {
      dispatch(setSelectedLicenseTypeId(null));
      dispatch(resetConfiguration());
    }

    dispatch(setLpco(null)); // in case of imported lpco data;
    dispatch(resetErrors());
  };

  return (
    <>
      <LicenseField.SelectField
        label={
          <Label helpText={t('Type of document (License, Permit, Certificate or other) you are applying for')}>
            {t('Type of {{licenseAbbreviation}}', { licenseAbbreviation: t(licenseAbbreviation) })}
          </Label>
        }
        onChange={(value) => handleLicenseChange(value as Nullable<SelectOption & { id: number }>)}
        placeholder={t('Choose license type')}
        disabled={!createMode || isChildLpco}
        name={HeaderTabFields.licenseType}
        isLoading={licenseTypesLoading}
        className={styles.licenseType}
        options={licenseTypeOptions}
        isClearable
      />
      {featureFlags?.relatedProductsEnabled && (
        <LicenseField.TextField
          label={
            <Label helpText={t('Products associated with this license application')}>{t('Related Products')}</Label>
          }
          name={HeaderTabFields.relatedProducts}
          placeholder={t('Related Products')}
          className={styles.relatedProducts}
          value={relatedProducts}
          disabled
          textarea
        />
      )}

      <LicenseField.TextField
        label={
          <Label helpText={t('Unique identifier provided to the user for reference')}>
            {t('User Reference Number')}
          </Label>
        }
        configName={HeaderTabFields.userReferenceNumber}
        placeholder={t('Write User Reference Number')}
        name={HeaderTabFields.userReferenceNumber}
        className={styles.firstColumn}
      />
      {featureFlags?.flowEnabled && (
        <LicenseField.TextField
          label={<Label helpText={t('Direction or Movement of goods')}>{t('Flow')}</Label>}
          name={HeaderTabFields.flow}
          placeholder={t('Flow')}
          value={t(flow)}
          disabled
        />
      )}
    </>
  );
};

export default MainInfoFields;
